/**
 * `useTeam` — TanStack Query hook for a single team's metadata.
 *
 * Issue #122. The breadcrumb chain on cycle-scoped routes
 * (`/cycles/:cycleId/...`) needs the team's name as the second
 * segment. The cycle response carries `team_id`, so the chain is
 * `cycleId → useCycle → team_id → useTeam → team.name`.
 *
 * Cache-key shape mirrors the inline `useQuery({ queryKey: ["team",
 * teamId, accountId], ... })` already living inside `<CyclePage/>`,
 * `<TeamPage/>`, `<TeamSetupLayout/>`, `<TeamAuditPage/>` — every
 * consumer shares the cache so the breadcrumb fetch warms the
 * page-body fetch (and vice-versa).
 *
 * `staleTime: 60_000` — team metadata (name + role) doesn't churn
 * intra-session; mirrors `useCycle`'s window so both keep the
 * breadcrumb stable as the lead navigates.
 *
 * `enabled: Boolean(teamId && accountId)` — no fetch fires for the
 * logged-out path or before the team_id has been resolved (e.g.
 * while a cycle query is still in flight).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { workspacesKey } from "../lib/workspacesKey";
import { type ApiError, apiFetch } from "./client";

/**
 * Minimal team row exposed by this hook. The full
 * `components["schemas"]["TeamResponse"]` shape carries 10+ fields
 * the breadcrumb consumers don't need — narrowing here keeps the type
 * surface small.
 *
 * Wave-6D (#489) — `has_on_call` + `on_call_label` carry the team's
 * on-call vocabulary config. The wave-6 Statistics dashboard reads
 * these to swap the on-call column label / heatmap-metric dropdown
 * option / table-mode cell label, AND to hide on-call surfaces
 * entirely when `has_on_call=false`. Optional in the type so
 * consumers that pre-date wave-6D still compile; the SPA helper
 * defaults to "on-call shown / labelled 'On-call'" when missing,
 * matching the backend's server defaults.
 */
export interface TeamSummary {
  id: string;
  name: string;
  role: string;
  transparency_mode?: string;
  has_on_call?: boolean;
  on_call_label?: string;
  // Issue #906 — the team's IANA timezone (e.g. "Asia/Singapore"). Used to
  // render note timestamps as date + HH:MM in the team's local time.
  // Optional so consumers that pre-date #906 still compile; the formatter
  // falls back to the viewer's zone when missing.
  timezone?: string;
  // #2060 — read by the shell for an observed team (its kind picks the nav).
  team_kind?: string;
  is_demo?: boolean;
}

const STALE_TIME_MS = 60_000;

export function useTeam(teamId: string | undefined) {
  const { accountId } = useAuth();
  return useQuery({
    queryKey: ["team", teamId ?? "", accountId ?? ""],
    enabled: Boolean(teamId && accountId),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<TeamSummary>(`/api/teams/${teamId}`),
  });
}

/**
 * `useRenameTeam` — lead mutation that renames a team (#2836), backed by
 * `PATCH /api/teams/{team_id}`.
 *
 * On success it writes the returned team into the `["team", …]` cache and
 * invalidates the Home teams list (`["teams"]`) and the admin console
 * queries, so every surface shows the new name at once.
 */
export function useRenameTeam(teamId: string | undefined) {
  const { accountId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<TeamSummary, ApiError, string>({
    mutationFn: (name: string) =>
      apiFetch<TeamSummary>(`/api/teams/${teamId}`, {
        method: "PATCH",
        body: JSON.stringify({ name }),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(["team", teamId ?? "", accountId ?? ""], data);
      void queryClient.invalidateQueries({ queryKey: ["teams"] });
      void queryClient.invalidateQueries({ queryKey: ["admin"] });
      void queryClient.invalidateQueries({ queryKey: workspacesKey(accountId) });
    },
  });
}

/**
 * `useSetTeamTimezone` — lead-only mutation for the team's IANA timezone
 * (#906), backed by `PATCH /api/teams/{team_id}/timezone`.
 *
 * On success it writes the returned team into the `["team", …]` query cache
 * so the Settings row + every note-timestamp surface reflect the new zone
 * without a refetch. A non-IANA value 422s server-side; the caller can read
 * `error` to surface that.
 */
export function useSetTeamTimezone(teamId: string | undefined) {
  const { accountId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<TeamSummary, Error, string>({
    mutationFn: (timezone: string) =>
      apiFetch<TeamSummary>(`/api/teams/${teamId}/timezone`, {
        method: "PATCH",
        body: JSON.stringify({ timezone }),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(["team", teamId ?? "", accountId ?? ""], data);
    },
  });
}
